/* eslint-disable jira/naming/disallow-filenames */

import React from 'react';
import { PRODUCT_DISCOVERY_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { EmbedModeProvider } from '@jira/issue__issue-view-embed-mode/src/EmbedModeProvider.tsx';
import {
	useProjectKey,
	// eslint-disable-next-line jira/restricted/unsafe-global-state-stores
	useProjectType,
	// eslint-disable-next-line jira/restricted/unsafe-global-state-stores
	useProjectId,
} from '@jira/issue__project-context-service/src/useProjectKey.tsx';
import type IssuePageType from '@jira/spa-apps__issue/src/SpaAppsIssue.tsx';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { markAsBentoBundleReady } from '@jira/platform__spa/src/services/mark-bento-bundle-as-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { Redirect, usePathParam } from '@atlassian/react-resource-router';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyIssuePage: LazyComponent<React.ComponentType<Record<any, any>>> = lazyForPaint<
	typeof IssuePageType
>(
	() =>
		markAsBentoBundleReady(
			// chunk name here MUST be kept in sync with TempLazyIssuePageWithSsrAlwaysDisabled below
			import(
				/* webpackChunkName: "async-issue-app--full-page", jiraSpaEntry: "spa-issue" */ '@jira/spa-apps__issue/src/IssueViewWithRedirect.tsx'
			).then((module) => module.IssueView),
		),
	{ entryFsSsrLiftImportToModule: true },
);

export const IssueComponent = (): React.JSX.Element => {
	const { data } = useProjectContext();
	const [issueKey] = usePathParam('issueKey');
	const pageId = 'spa-issue';

	const [projectKey, issueSuffix] = issueKey !== undefined ? issueKey.split('-') : [];
	let actualIssueKey = issueKey;
	if (data?.projectKey && data.projectKey !== projectKey) {
		actualIssueKey = `${data.projectKey}-${issueSuffix}`;
	}

	// special handling for product discovery (=== Polaris) project issues. During the experimental phase of
	// the polaris idea view (product-specific issue view), we will redirect to a polaris-specific issue view
	// page here instead of bento.
	// this will only happen for issues of this specific project type, and only if the switchover FF is enabled
	if (
		!__SERVER__ &&
		data?.projectType === PRODUCT_DISCOVERY_PROJECT &&
		data?.projectKey !== undefined &&
		actualIssueKey !== undefined
	) {
		return (
			<Redirect
				to={`/jira/polaris/projects/${data.projectKey}/ideas?selectedIssue=${actualIssueKey}`}
			/>
		);
	}

	return <LazyPage Page={LazyIssuePage} pageId={pageId} shouldShowSpinner={false} />;
};

export const IssueEmbedComponent = (): React.JSX.Element => {
	const [issueKey] = usePathParam('issueKey');
	const projectKey = useProjectKey(issueKey || '');
	const projectType = useProjectType(projectKey);
	const projectId = useProjectId(projectKey);

	// Special handling for product discovery (=== Polaris) project issues in embed mode.
	// We have to redirect to Polaris issue embed view instead of loading Jira issue view.
	if (
		projectType === PRODUCT_DISCOVERY_PROJECT &&
		projectKey !== undefined &&
		projectId !== undefined &&
		issueKey !== undefined
	) {
		return (
			<Redirect
				to={`/secure/JiraProductDiscoveryEmbedded.jspa?projectId=${projectId}&projectKey=${projectKey}&issueKey=${issueKey}`}
			/>
		);
	}

	return (
		<EmbedModeProvider>
			<IssueComponent />
		</EmbedModeProvider>
	);
};
